
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Wallet, Shield, FileText, Sparkles, BarChart3, Camera, TrendingUp } from "lucide-react"

interface AboutProps {
  onBack: () => void
}

export function About({ onBack }: AboutProps) {
  const features = [
    {
      icon: Sparkles,
      title: "Smart Tracking",
      description: "Easily track expenses and income with intuitive interface"
    },
    {
      icon: Camera,
      title: "Receipt Scanning",
      description: "Scan receipts and automatically extract transaction details"
    },
    {
      icon: BarChart3,
      title: "Analytics",
      description: "Visualize your spending patterns with detailed charts"
    },
    {
      icon: TrendingUp,
      title: "Budget Goals",
      description: "Set and track financial goals for better money management"
    }
  ]

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-md space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">About</h1>
            <p className="text-sm text-muted-foreground">Learn more about this app</p>
          </div>
        </div>

        {/* App Info Card */}
        <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Wallet className="h-10 w-10" />
              </div>
              <div>
                <h2 className="text-3xl font-bold">Expense Tracker</h2>
                <p className="text-sm opacity-90 mt-2">Version 1.0.0</p>
              </div>
              <p className="text-sm opacity-90 max-w-sm">
                Your personal finance companion for smart money management
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>Key Features</CardTitle>
            <CardDescription>What makes this app special</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <div key={feature.title} className="flex gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>

        {/* Legal */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Legal
            </CardTitle>
            <CardDescription>Terms and privacy information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="ghost" className="w-full justify-start gap-3">
              <FileText className="h-4 w-4" />
              Terms of Service
            </Button>
            <Button variant="ghost" className="w-full justify-start gap-3">
              <Shield className="h-4 w-4" />
              Privacy Policy
            </Button>
          </CardContent>
        </Card>

        {/* Copyright */}
        <div className="text-center pb-6">
          <p className="text-sm text-muted-foreground">
            © 2025 Expense Tracker. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
